/* eslint-disable @typescript-eslint/no-unused-vars */
import { PrinterOutlined, UploadOutlined } from "@ant-design/icons";
import { Button, Col, Form, Input, Modal, Typography, Upload } from "antd";
import moment from "moment";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useModalForm as useModalFormAntd } from "sunflower-antd/lib/useModalForm";
import { ROOT_URL } from "../../App";
import { SelectField } from "../../components/inputs/select/select";
import {
  getInvoiceStatusOptionLabel,
  paymentOrderInvoiceReceivablePossibleMoves,
} from "../../components/inputs/select/statusSelect.constants";
import TextContent from "../../components/modal/admin/text-content";
import { TOKEN_KEY } from "../../components/providers/authProvider";
import { IBankAccount } from "../../interfaces/bankAccounts";
import { IBank } from "../../interfaces/banks";
import { ICompany } from "../../interfaces/companies";
import { ICustomer } from "../../interfaces/customer";
import { IRemitter } from "../../interfaces/remitter";
import PaymentStatus from "../../types/paymentStatus.type";
import { acceptUploadFileTypes } from "../../utils/acceptUploadFileTypes";
import { beforeUpload } from "../../utils/beforeUpload";
import {
  clearFieldErrorMessageOnChange,
  getFileListFromEvent,
} from "../../utils/form.constants";
import { useNotificationOkCancel } from "../../utils/hooks/useNotificationOkCancel.hook";
import { renderWorldcheckVerificationStatus } from "../../utils/renderWorldCheckVerificationStatus";
import { ButtonListFooter } from "../components/buttonsList";
import { confirmModalUnsavedChangesProps } from "../components/constants";
import { IDocumentsChanges, IPaymentFormValues } from "../components/interface";
import ICurrency from "../currencies/currency.interface";
import { uploadButtonStyle } from "../styles/style";
import "./EditInvoiceReceivable.css";
import { useInvoiceReceivable } from "./useInvoiceReceivable";
import { useUpdateInvoiceReceivable } from "./useUpdateInvoiceReceivable";
import { purposeOfRemittanceItemNames } from "../../components/inputs/select/purposeOfRemittance.constants";
import { getDecimalsBySymbol } from "../../utils/list.utils";

export interface InvoiceReceivable {
  id?: number;
  status: PaymentStatus;
  documentNames?: string[];
  user: {
    id: number;
    fullName: string;
  };
  paymentOrder: {
    id: number;
    dateOfTransaction: Date;
    amount: number | null;
    bank: IBank | null;
    company: ICompany | null;
    currency?: ICurrency | null;
    bankAccount?: IBankAccount | null;
    specialInstructions: string | null;
    remitter: IRemitter | null;
    customer?: ICustomer | null;
  };
  printableFormUrl?: string;
  emailedInvoiceRead?: boolean;
}

interface Props {
  editingId?: number;
  onClose: () => void;
  onComplete: () => void;
}

export const Crypto = ["USDT", "DAI", "ETH"];

export const EditInvoiceReceivable: FC<Props> = ({
  editingId,
  onClose,
  onComplete,
}) => {
  const { data, isLoading, fetch } = useInvoiceReceivable(editingId);
  const [formChanged, setFormChanged] = useState(false);

  const defaultFormValues = useMemo(() => {
    if (data) {
      const {
        paymentOrder: {
          dateOfTransaction = undefined,
          amount = undefined,
          currency = undefined,
          company = undefined,
          bank = undefined,
          bankAccount = undefined,
          specialInstructions = undefined,
          remitter,
          reason,
          beneficiaryWalletNetworkName = undefined,
          beneficiaryWalletAddress = undefined,
          beneficiaryBankName = undefined,
          beneficiaryBankSwift = undefined,
          beneficiaryBankAccountNumber = undefined,
          beneficiaryBankAddress = undefined,
        } = {},
        documentNames,
        status,
      } = data;

      return {
        dateOfTransaction,
        amount,
        currencyCode: currency && `${currency.code} - ${currency.name}`,
        companyId: company?.name,
        bankId: beneficiaryBankName ?? bank?.name,
        bankAccountId:
          (beneficiaryBankAccountNumber ?? bankAccount) &&
          `${(beneficiaryBankAccountNumber ?? bankAccount?.accountNumber)} [${(beneficiaryBankAccountNumber ?? bankAccount?.currencyCode)}]`,
        specialInstructions,
        documents: documentNames?.map((fileName: string) => ({
          name: fileName,
        })),
        reason,
        remitterId: remitter?.name,
        status,
      };
    } else {
      return undefined;
    }
  }, [data]);

  const { close, modalProps, formProps, show } = useModalFormAntd({});
  const status = Form.useWatch("status", { form: formProps.form });

  useEffect(() => {
    formProps?.form?.setFieldsValue(defaultFormValues);
  }, [defaultFormValues, formProps?.form]);

  useEffect(() => {
    if (editingId !== undefined) {
      show();
    } else {
      close();
    }
  }, [close, editingId, show]);

  const { update, isLoading: isUpdating } =
    useUpdateInvoiceReceivable(editingId);

  const [initialDocuments, setInitialDocuments] = useState<string[]>([]);
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus>(
    PaymentStatus.DRAFT
  );
  const [remitterData, setRemitterData] = useState<IRemitter>();

  const formIsLoading = isLoading || isUpdating;

  useEffect(() => {
    if (data) {
      const {
        paymentOrder: {
          customer = undefined,
          currency = undefined,
          company = undefined,
          bank = undefined,
          bankAccount = undefined,
          remitter,
        } = {},
        status,
        documentNames,
      } = data;

      setInitialDocuments(documentNames ?? []);
      if (status) {
        setPaymentStatus(status);
      }
      if (remitter) {
        setRemitterData(remitter);
      }
    }
  }, [data]);

  const onSubmit = useCallback(
    async (formValues: IPaymentFormValues & { status: PaymentStatus }) => {
      if (!editingId) {
        return;
      }

      const documentChanges: IDocumentsChanges = {
        new: formValues.documents
          ?.filter((document) => document.response)
          .map(({ response }) => response ?? "N/A"),
        deleted: initialDocuments.filter(
          (initialFileName) =>
            !formValues.documents?.find(({ name }) => {
              return name === initialFileName;
            })
        ),
      };

      const isSuccess = await update({
        specialInstructions: formValues.specialInstructions,
        documentChanges,
        status: formValues.status,
        reason:
          formValues.status === PaymentStatus.APPROVED ||
            formValues.status === PaymentStatus.PENDING
            ? null
            : formValues?.reason,
      });

      if (isSuccess) {
        fetch();
        onComplete();
      }
    },
    [editingId, initialDocuments, update, fetch, onComplete]
  );

  const onUnsavedChangesOk = useCallback(() => {
    formProps?.form?.resetFields();
    setRemitterData(undefined);
    setFormChanged(false);
    onClose();
  }, [formProps?.form, onClose]);

  const {
    openNotification: openUnsavedChangesNotification,
    contextHolder: unsavedChangesContextHolder,
  } = useNotificationOkCancel({
    ...confirmModalUnsavedChangesProps,
    onOk: onClose,
  });

  const statusSelectItems = useMemo(
    () =>
      paymentOrderInvoiceReceivablePossibleMoves.get(
        data?.status || PaymentStatus.DRAFT
      ) || [PaymentStatus.DRAFT],
    [data?.status]
  );

  const onModalCancel = useCallback(() => {
    if (formIsLoading) return;
    onClose();
  }, [onClose, formIsLoading]);

  const isDisabledForm =
    paymentStatus !== PaymentStatus.PENDING &&
    paymentStatus !== PaymentStatus.POSTPONED;

  const isButtonsDisabled =
    paymentStatus === PaymentStatus.COMPLETED ||
    paymentStatus === PaymentStatus.APPROVED ||
    paymentStatus === PaymentStatus.PROCESSING ||
    paymentStatus === PaymentStatus.REJECTED ||
    paymentStatus === PaymentStatus.FAILED;

  const formatNumber = useCallback((value: number | string, decimals?: number) => {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(num)) return typeof decimals === 'number' ? (0).toFixed(decimals) : '0';
    if (typeof decimals === 'number') {
      return num.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
    }
    return num.toLocaleString('en-US');
  }, []);

  return (
    <>
      <Modal
        {...modalProps}
        onCancel={onModalCancel}
        title="Edit invoice"
        className="smartModalTitle"
        afterClose={onUnsavedChangesOk}
        destroyOnClose
        maskClosable={false}
        footer={
          <Col>
            {data?.emailedInvoiceRead !== null && (
              <span>
                Emailed Invoice Status:
                {!data?.emailedInvoiceRead ? (
                  <span style={{ color: "#595959" }}> Unread</span>
                ) : (
                  <span style={{ color: "#52C41A" }}> Read</span>
                )}
              </span>
            )}
            <ButtonListFooter
              handleSave={formProps?.form?.submit}
              handleSend={onModalCancel}
              isLoading={formIsLoading}
              hideSendBtn={true}
              isSaveBtnDisabled={!formChanged}
              isDisabled={isButtonsDisabled || !formChanged || formIsLoading}
            />
            {data?.printableFormUrl && (
              <Button
                icon={<PrinterOutlined />}
                type={"link"}
                href={data?.printableFormUrl}
                target={"_blank"}
              >
                &nbsp;Print
              </Button>
            )}
          </Col>
        }
      >
        {unsavedChangesContextHolder}
        <Form
          {...formProps}
          layout="vertical"
          onFinish={onSubmit}
          validateTrigger={false}
          onValuesChange={(e) => {
            setFormChanged(true);
            clearFieldErrorMessageOnChange(formProps?.form)(e);
          }}
          disabled={isDisabledForm}
        >
          <div
            style={{
              display: "flex",
              width: "100%",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography className="text-label" style={{ textAlign: "start" }}>
              Request Money order ID:
            </Typography>
            <Typography className="text-label" style={{ textAlign: "start" }}>
              #{data?.paymentOrder.id}
            </Typography>
          </div>
          <hr />
          {/* <TextContent
            label="Send Date"
            value={moment(data?.paymentOrder?.dateOfTransaction).format(
              "DD/MM/YYYY"
            )}
          /> */}
          <TextContent
            label="Total to pay"
            value={
              `${data?.paymentOrder?.totalSendAmount
                ? formatNumber(data?.paymentOrder?.totalSendAmount, getDecimalsBySymbol(data?.paymentOrder?.paymentCurrency?.symbol ?? ''))
                : 0
              } ${data?.paymentOrder?.paymentCurrency?.symbol}`
            }
            boldValue
          />
          <div
            style={{
              display: "flex",
              width: "100%",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "end",
            }}
          >
            <Typography
              className="text-label"
              style={{
                textAlign: "start",
                fontSize: "12px",
                color:
                  data?.paymentOrder?.isPaid ||
                    paymentStatus === PaymentStatus.COMPLETED
                    ? "#18B853"
                    : "rgba(242, 138, 40, 1)",
              }}
            >
              {data?.paymentOrder?.isPaid ||
                paymentStatus === PaymentStatus.COMPLETED
                ? "*User has paid"
                : "*User has not paid"}
            </Typography>
          </div>
          <TextContent
            label="Beneficiary will receive"
            value={
              `${data?.paymentOrder?.totalReceiveAmount
                ? formatNumber(data?.paymentOrder?.totalReceiveAmount, getDecimalsBySymbol(data?.paymentOrder?.receiveCurrency?.symbol ?? ''))
                : 0
              } ${data?.paymentOrder?.receiveCurrency?.symbol}`
            }
            boldValue
          />
          <hr />

          {!data?.paymentOrder?.providerWallet &&
            !data?.paymentOrder?.providerBank &&
            !data?.paymentOrder?.providerBankAccount &&
            !data?.paymentOrder?.providerWalletNetworkName &&
            !data?.paymentOrder?.providerWalletAddress &&
            !data?.paymentOrder?.providerBankName &&
            !data?.paymentOrder?.providerBankSwift &&
            !data?.paymentOrder?.providerBankAddress &&
            !data?.paymentOrder?.providerBankAccountNumber ? (
            <>
              <TextContent label="Payment Method" value="Portfolio" />
              <TextContent
                label="Currency"
                value={data?.paymentOrder?.paymentCurrency?.symbol || "N/A"}
              />
            </>
          ) : (
            <>
              <TextContent label="Payment Method" value="Provider" />
              <TextContent
                label="Currency"
                value={data?.paymentOrder?.receiveCurrency?.symbol}
              />
              <TextContent
                label="Provider"
                value={data?.paymentOrder?.serviceProvider?.name}
              />
              {Crypto.includes(data?.paymentOrder?.paymentCurrency?.symbol) ? (
                <>
                  <TextContent
                    label="Provider Wallet Network"
                    value={data?.paymentOrder?.providerWalletNetworkName ?? data?.paymentOrder?.providerWallet?.network?.name}
                  />
                  <TextContent
                    label="Provider Wallet Address"
                    value={data?.paymentOrder?.providerWalletAddress ?? data?.paymentOrder?.providerWallet?.address}
                  />
                </>
              ) : (
                <>
                  <TextContent
                    label="Provider Bank Name"
                    value={data?.paymentOrder?.providerBankName ?? data?.paymentOrder?.providerBank?.name}
                  />
                  <TextContent
                    label="Provider Bank SWIFT"
                    value={data?.paymentOrder?.providerBankSwift ?? data?.paymentOrder?.providerBank?.swift}
                  />
                  <TextContent
                    label="Provider Bank Address"
                    value={data?.paymentOrder?.providerBankAddress ?? data?.paymentOrder?.providerBank?.legalAddress}
                  />
                  <TextContent
                    label="Provider Bank Account"
                    value={data?.paymentOrder?.providerBankAccountNumber ?? data?.paymentOrder?.providerBankAccount?.accountNumber}
                  />
                </>
              )}
            </>
          )}
          {!data?.paymentOrder?.providerWallet &&
            !data?.paymentOrder?.providerBank &&
            !data?.paymentOrder?.providerBankAccount &&
            !data?.paymentOrder?.providerWalletNetworkName &&
            !data?.paymentOrder?.providerWalletAddress &&
            !data?.paymentOrder?.providerBankName &&
            !data?.paymentOrder?.providerBankSwift &&
            !data?.paymentOrder?.providerBankAddress &&
            !data?.paymentOrder?.providerBankAccountNumber ? (
            <>
            </>
          ) : (
            <>
              <TextContent
                label="Future date"
                value={
                  data?.paymentOrder?.futurePaymentDate
                    ? moment(
                      data?.paymentOrder?.futurePaymentDate
                    ).format("DD/MM/YYYY")
                    : ""
                }
              />
            </>
          )}
          <hr />
          <TextContent
            label="Remitter"
            value={(data?.paymentOrder?.remitterFullName ?? data?.paymentOrder?.remitter?.name) || "N/A"}
            boldValue={true}
            boldLabel={true}
          />
          {remitterData?.worldCheckVerification &&
            renderWorldcheckVerificationStatus(
              remitterData.worldCheckVerification.status
            )}
          <TextContent
            label="Address"
            value={(data?.paymentOrder?.remitterCompanyAddress ?? data?.paymentOrder?.remitter?.address1) || "N/A"}
          />
          <TextContent
            label="Country"
            value={(data?.paymentOrder?.remitterCountry ?? data?.paymentOrder?.remitter?.country) || "N/A"}
          />
          <TextContent
            label="VAT/TIN/UID"
            value={(data?.paymentOrder?.remitterTinVatUid ?? data?.paymentOrder?.remitter?.tinVatUid) || "N/A"}
          />
          <TextContent
            label="Email"
            value={(data?.paymentOrder?.remitterEmail ?? data?.paymentOrder?.remitter?.email) || "N/A"}
          />
          <TextContent
            label="Website"
            value={(data?.paymentOrder?.remitterWebsite ?? data?.paymentOrder?.remitter?.website) || "N/A"}
          />
          <hr />
          <TextContent
            label="Customer"
            value={data?.paymentOrder?.customer?.fullName || ""}
            boldValue={true}
            boldLabel={true}
          />
          <TextContent
            label="Name"
            value={data?.paymentOrder?.customer?.email || "N/A"}
          />
          <TextContent
            label="SMART UID"
            value={data?.paymentOrder?.customer?.smartUID || "N/A"}
          />
          <hr />
          <TextContent
            label="Special instructions"
            value={data?.paymentOrder?.specialInstructions || "N/A"}
          />
          {data?.paymentOrder.purposeOfRemittance && (
            <TextContent
              label="Purpose Of Remittance"
              value={
                purposeOfRemittanceItemNames[
                data?.paymentOrder.purposeOfRemittance
                ] || "N/A"
              }
            />
          )}
          <SelectField
            name="status"
            getOptionLabel={getInvoiceStatusOptionLabel}
            items={statusSelectItems}
            disabled={isDisabledForm || formIsLoading}
          />
          {status === PaymentStatus?.REJECTED ||
            status === PaymentStatus.POSTPONED ? (
            <Form.Item
              name="reason"
              rules={[{ required: true, message: "This field is required", whitespace: true }]}
            >
              <Input.TextArea
                rows={2}
                placeholder="Reason"
                allowClear
                disabled={
                  data?.status === PaymentStatus.REJECTED ||
                  data?.status === PaymentStatus.POSTPONED ||
                  formIsLoading
                }
                value={data?.paymentOrder?.reason}
              />
            </Form.Item>
          ) : null}
          <Col>
            <Form.Item
              name="documents"
              valuePropName="fileList"
              getValueFromEvent={getFileListFromEvent}
            >
              <Upload
                accept={acceptUploadFileTypes}
                action={`${ROOT_URL}/api/v1/storage/upload`}
                listType="text"
                headers={{
                  Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}`,
                }}
                multiple
                beforeUpload={beforeUpload}
                showUploadList={{
                  showRemoveIcon:
                    paymentStatus !== PaymentStatus.DRAFT &&
                      paymentStatus !== PaymentStatus.PENDING
                      ? false
                      : true,
                }}
              >
                <Button
                  disabled={
                    paymentStatus !== PaymentStatus.PENDING || formIsLoading
                  }
                  icon={<UploadOutlined />}
                  style={uploadButtonStyle}
                >
                  &nbsp;Click to upload supporting documents
                </Button>
              </Upload>
            </Form.Item>
          </Col>
        </Form>
      </Modal>
    </>
  );
};
